// src/features/users/hooks/useUser.js
import { useContext, useCallback } from "react";
import { UserContext } from "../contexts/UserContext";

/**
 * Custom hook for user management operations
 * Provides access to user data and operations
 */
export function useUser() {
  const context = useContext(UserContext);

  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }

  // Destructure the core functions and state from context
  const {
    users,
    loading,
    error,
    selectedUser,
    pendingApprovals,
    fetchUsers,
    fetchPendingApprovals,
    fetchUserById,
    updateUserStatus,
    updateUser,
  } = context;

  // Add any derived convenience functions
  const setUserActive = useCallback(
    async userId => {
      return await updateUserStatus(userId, 2); // 2 = Active status
    },
    [updateUserStatus]
  );

  const setUserInactive = useCallback(
    async userId => {
      return await updateUserStatus(userId, 3); // 3 = Inactive status
    },
    [updateUserStatus]
  );

  const deleteUser = useCallback(
    async userId => {
      return await updateUserStatus(userId, 4); // 4 = Deleted status
    },
    [updateUserStatus]
  );

  // Create consistent API that components will use
  return {
    users,
    loading,
    error,
    selectedUser,
    pendingApprovals,
    fetchUsers,
    fetchPendingApprovals,
    fetchUserById,
    updateUser,
    setUserActive,
    setUserInactive,
    deleteUser,
  };
}
